import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import { ComicTemplate, ComicPanel as ComicPanelType, DialogBubbleData, ImageAsset } from '../types'
import { ComicPanel } from './ComicPanel'

interface ComicCanvasProps {
  template: ComicTemplate
  panels: ComicPanelType[]
  dialogBubbles: DialogBubbleData[]
  selectedBubble: string | null
  selectedPanel: string | null
  onSelectBubble: (id: string | null) => void
  onSelectPanel: (id: string) => void
  onUpdateBubble: (id: string, updates: Partial<DialogBubbleData>) => void
  onDeleteBubble: (id: string) => void
  onUpdatePanel: (id: string, updates: Partial<ComicPanelType>) => void
  onShowPanel: () => void
  onSetPanelBg: (panelId: string, imageUrl: string) => void
  onAddPanel: () => void
  imageAssets: ImageAsset[]
  backgroundColor?: string
  title?: string
  watermark?: string
}

export const ComicCanvas = React.forwardRef<HTMLDivElement, ComicCanvasProps>(
  (props, ref) => {
    const {
      template,
      panels,
      dialogBubbles,
      selectedBubble,
      onSelectBubble,
      onUpdateBubble,
      onDeleteBubble,
      onUpdatePanel,
      onShowPanel,
      onAddPanel
    } = props

    const { setNodeRef: setDroppableRef } = useDroppable({
      id: 'comic-canvas',
    })

    const handleCanvasClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onSelectBubble(null)
      }
    }

    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gradient">画布编辑区</h2>
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span>模板: {template.name}</span>
            <span>面板: {panels.length}</span>
            <span>对话框: {dialogBubbles.length}</span>
            <button
              onClick={onAddPanel}
              className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium shadow-lg transition-colors"
              title="添加新面板"
            >
              <Plus size={16} />
              添加面板
            </button>
            {props.selectedPanel && (
              <span className="px-3 py-1 bg-blue-500 text-white rounded text-sm font-medium shadow flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                正在工作: 面板{panels.findIndex(p => p.id === props.selectedPanel) + 1}
              </span>
            )}
          </div>
        </div>
        
        {/* 使用固定高度和滚动容器，防止画布被挤压 */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4 min-h-0">
          <div className="relative flex-shrink-0">
            {/* 标题 - 画布外部上方 */}
            {props.title && (
              <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-10">
                <div className="text-2xl font-bold text-gray-800 drop-shadow-lg select-none pointer-events-none text-center whitespace-nowrap">
                  {props.title}
                </div>
              </div>
            )}
            
            <div 
              ref={(node) => {
                // 设置 forwardRef 的 ref
                if (typeof ref === 'function') {
                  ref(node)
                } else if (ref) {
                  ref.current = node
                }
                // 设置 droppable 的 ref
                setDroppableRef(node)
              }}
              className="relative bg-white rounded-lg shadow-xl border-2 border-gray-200 overflow-hidden flex-shrink-0"
              style={{
                width: template.width,
                height: template.height,
                minWidth: template.width,
                minHeight: template.height,
                backgroundColor: props.backgroundColor || '#fff'
              }}
              onClick={handleCanvasClick}
            >
              
              {/* 渲染所有面板 */}
              {panels.map((panel) => (
                <ComicPanel
                  key={panel.id}
                  panel={panel}
                  dialogBubbles={dialogBubbles}
                  selectedBubble={selectedBubble}
                  selectedPanel={props.selectedPanel}
                  onSelectBubble={onSelectBubble}
                  onSelectPanel={props.onSelectPanel}
                  onUpdateBubble={onUpdateBubble}
                  onDeleteBubble={onDeleteBubble}
                  onUpdatePanel={onUpdatePanel}
                  onShowPanel={onShowPanel}
                  onSetPanelBg={props.onSetPanelBg}
                  imageAssets={props.imageAssets}
                />
              ))}
            </div>
            
            {/* 水印 - 画布外部右下角 */}
            {props.watermark && (
              <div className="absolute -bottom-8 right-0 z-10">
                <div className="text-xs text-gray-400 select-none pointer-events-none whitespace-nowrap">
                  {props.watermark}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
)

ComicCanvas.displayName = 'ComicCanvas' 